import React from 'react';
import { Box, ChipProps, Dialog, DialogContent, IconButton, Popover, Typography } from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import { useChipMenuStyles } from './styles';
import { useUpBreakpoint } from '@uplift/hooks';
import { LightChip } from '../LightChip';
import { Chip } from '../Chip/Chip.component';

export type ChipMenuProps = Omit<ChipProps, 'onClose' | 'children' | 'title'> & {
  light?: boolean;
  contentTitle: React.ReactNode;
  onClose?: () => void;
};

export const ChipMenu: React.FC<ChipMenuProps> = ({ light, contentTitle, onClose, children, ...props }) => {
  const { classes } = useChipMenuStyles();
  const [ open, setOpen ] = React.useState(false);
  const anchor = React.useRef<HTMLDivElement>(null);
  const isSmUp = useUpBreakpoint('sm');

  const handleOpen = React.useCallback(() => {
    setOpen(x => !x);

    if (!open && onClose) {
      onClose();
    }
  }, [ onClose, open ]);

  const ChipComponent = React.useMemo(() => light ? LightChip : Chip, [ light ]);

  return (
    <>
      <ChipComponent {...props} onClick={handleOpen} ref={anchor} />
      {isSmUp ? (
        <Popover
          open={open}
          anchorEl={anchor?.current}
          onClose={handleOpen}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
        >
          <Box className={classes.content} onClick={handleOpen}>
            <Box mb={1}>
              <Typography variant="body1">
                {contentTitle}
              </Typography>
            </Box>
            {children}
          </Box>
        </Popover>
      ) : (
        <Dialog fullScreen open={open}>
          <Box className={classes.title}>
            <Typography variant="h3">
              {contentTitle}
            </Typography>
            <IconButton aria-label="close" onClick={handleOpen} size="small">
              <CloseOutlined />
            </IconButton>
          </Box>
          <DialogContent className={classes.content} onClick={handleOpen}>
            {children}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
